import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface FrequencyBasedResponse {
  approved: number;
  evaluating: number;
  blocked: number;
  total: number;
}

interface MonthlySeriesData {
  name: string;
  data: number[];
}

interface CachedData<T> {
  data: T | null;
  lastRefreshed: number | null; // Unix timestamp
  jobId?: string; // For tracking refresh jobs
}

interface StatsState {
  yearlyData: CachedData<MonthlySeriesData[]>;
  frequencyData: {
    monthly: CachedData<FrequencyBasedResponse>;
    yearToDate: CachedData<FrequencyBasedResponse>;
    allTime: CachedData<FrequencyBasedResponse>;
    last60days: CachedData<FrequencyBasedResponse>;
  };
  isRefreshing: boolean;
  refreshError: string | null;
}

const createEmptyCachedData = <T>(): CachedData<T> => ({
  data: null,
  lastRefreshed: null,
});

const initialState: StatsState = {
  yearlyData: createEmptyCachedData<MonthlySeriesData[]>(),
  frequencyData: {
    monthly: createEmptyCachedData<FrequencyBasedResponse>(),
    yearToDate: createEmptyCachedData<FrequencyBasedResponse>(),
    allTime: createEmptyCachedData<FrequencyBasedResponse>(),
    last60days: createEmptyCachedData<FrequencyBasedResponse>(),
  },
  isRefreshing: false,
  refreshError: null,
};

type FrequencyKey = "monthly" | "yearToDate" | "allTime" | "last60days";

export const statsSlice = createSlice({
  name: "stats",
  initialState,
  reducers: {
    setYearlyData: (
      state,
      action: PayloadAction<{ data: MonthlySeriesData[]; timestamp?: number }>
    ) => {
      state.yearlyData = {
        data: action.payload.data,
        lastRefreshed: action.payload.timestamp || Date.now(),
      };
    },
    setYearlyJobId: (state, action: PayloadAction<string>) => {
      state.yearlyData.jobId = action.payload;
    },
    setFrequencyData: (
      state,
      action: PayloadAction<{
        frequency: FrequencyKey;
        data: FrequencyBasedResponse;
        timestamp?: number;
      }>
    ) => {
      const { frequency, data, timestamp } = action.payload;
      state.frequencyData[frequency] = {
        data,
        lastRefreshed: timestamp || Date.now(),
      };
    },
    setFrequencyJobId: (
      state,
      action: PayloadAction<{ frequency: FrequencyKey; jobId: string }>
    ) => {
      state.frequencyData[action.payload.frequency].jobId = action.payload.jobId;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.isRefreshing = action.payload;
      if (action.payload) {
        state.refreshError = null;
      }
    },
    setRefreshError: (state, action: PayloadAction<string | null>) => {
      state.refreshError = action.payload;
      state.isRefreshing = false;
    },
    clearCache: (state) => {
      state.yearlyData = createEmptyCachedData<MonthlySeriesData[]>();
      state.frequencyData = {
        monthly: createEmptyCachedData<FrequencyBasedResponse>(),
        yearToDate: createEmptyCachedData<FrequencyBasedResponse>(),
        allTime: createEmptyCachedData<FrequencyBasedResponse>(),
        last60days: createEmptyCachedData<FrequencyBasedResponse>(),
      };
    },
  },
});

export const {
  setYearlyData,
  setYearlyJobId,
  setFrequencyData,
  setFrequencyJobId,
  setRefreshing,
  setRefreshError,
  clearCache,
} = statsSlice.actions;

// Selectors
export const selectYearlyData = (state: { stats: StatsState }) =>
  state.stats.yearlyData;
export const selectFrequencyData = (frequency: FrequencyKey) => (state: { stats: StatsState }) =>
  state.stats.frequencyData[frequency];
export const selectIsRefreshing = (state: { stats: StatsState }) =>
  state.stats.isRefreshing;
export const selectRefreshError = (state: { stats: StatsState }) =>
  state.stats.refreshError;
export const selectLastRefreshed = (state: { stats: StatsState }) => {
  // Return the most recent timestamp from all cached data
  const timestamps = [
    state.stats.yearlyData.lastRefreshed,
    state.stats.frequencyData.monthly.lastRefreshed,
    state.stats.frequencyData.yearToDate.lastRefreshed,
    state.stats.frequencyData.allTime.lastRefreshed,
    state.stats.frequencyData.last60days.lastRefreshed,
  ].filter((t): t is number => t !== null);
  
  return timestamps.length > 0 ? Math.max(...timestamps) : null;
};

export default statsSlice.reducer;


